import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  useRxCollectionEpocusExam,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  type ExamType,
  NORMAL_WORKSHEET_BY_TYPE,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { useUser } from "$global-providers/user-provider/provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { nanoid } from "nanoid";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../../_lib/epocus-palette";
import { epocusTheme } from "../../../../_lib/epocus-theme-provider";
import {
  imagingInstanceToExamImage,
  useAssignedExamImageIds,
  useImagingInstancesByAccessionNumber,
} from "../../../../_lib/hooks/use-encounter-imaging-instances";
import { useEnabledExamTypeOptions } from "../../../../_lib/use-enabled-exam-types";
import { linkToEpocusEncounter, linkToEpocusExam } from "../../../../links";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const Card = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: 16,
  border: `1px solid ${EPOCUS_BORDER}`,
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const CardLabel = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: EPOCUS_TEXT_SECONDARY,
  textTransform: "uppercase",
});

const ExamGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
});

const ExamTypeBtn = styled(Button)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  padding: "12px 8px",
  borderRadius: 10,
  fontWeight: 500,
  fontSize: rem(14),
  minHeight: 72,
  textTransform: "none",
  border: `2px solid ${EPOCUS_BORDER}`,
  backgroundColor: EPOCUS_WHITE,
  color: EPOCUS_TEXT_PRIMARY,
});

const ExamIconWrap = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SubmitButton = styled(Button)({
  borderRadius: 10,
  padding: "14px 24px",
  fontWeight: 700,
  fontSize: rem(16),
  textTransform: "none",
  minHeight: 52,
});

export const EpocusAddExamPage: FC = () => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const examCollection = useRxCollectionEpocusExam();

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) => collection.findOne(encounterId ?? ""),
  );
  const encounter = encounters?.[0] ?? null;

  // The schema permits blank/whitespace accessions; normalize as the encounter
  // page does so a stored whitespace value can't make the query miss instances.
  const accessionNumber = encounter?.accessionNumber?.trim() || null;

  const { instances, isFetching: instancesFetching } =
    useImagingInstancesByAccessionNumber(accessionNumber);
  const { assignedImageIds, isFetching: assignedFetching } =
    useAssignedExamImageIds(encounterId);

  const [selectedExamType, setSelectedExamType] = useState<ExamType | null>(
    null,
  );
  const examTypeOptions = useEnabledExamTypeOptions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    if (!selectedExamType) {
      setError("Please select an exam type before continuing.");
      return;
    }

    if (!examCollection) {
      setError("Exam data is still loading. Please wait and try again.");
      return;
    }

    if (!encounterId) {
      setError(
        "Unable to add exam because the encounter could not be identified.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const examId = nanoid();
      const now = new Date().toISOString();
      const normalData = NORMAL_WORKSHEET_BY_TYPE[selectedExamType];
      const emptyViews = Object.fromEntries(
        Object.keys(normalData.views).map((k) => [k, false]),
      );
      const images = instances
        .filter((instance) => !assignedImageIds.has(instance.id))
        .map(imagingInstanceToExamImage);
      await examCollection.insert({
        id: examId,
        encounterId,
        signedAt: null,
        signedByUserId: null,
        nonBillable: false,
        nonBillableReason: null,
        amendments: [],
        examType: selectedExamType,
        status: "draft",
        worksheetData: JSON.parse(
          JSON.stringify({
            ...normalData,
            indication: null,
            views: emptyViews,
          }),
        ) as EpocusExam["worksheetData"],
        images,
        meta: {
          createdAt: now,
          createdByUserId: user.id,
          updatedAt: now,
          updatedByUserId: user.id,
        },
      });
      navigate(linkToEpocusExam(encounterId, examId));
    } catch (err) {
      setError("Failed to add exam. Please try again.");
      console.error("Add exam failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageRoot>
      <Button
        onClick={() => navigate(linkToEpocusEncounter(encounterId ?? ""))}
        style={{
          color: EPOCUS_PRIMARY,
          textTransform: "none",
          fontSize: rem(14),
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ‹ Back to encounter
      </Button>

      <Typography
        variant="h5"
        style={{
          fontWeight: 700,
          color: EPOCUS_TEXT_PRIMARY,
          fontSize: rem(24),
        }}
      >
        Add Exam
      </Typography>

      <Card>
        <CardLabel>Select Exam Type</CardLabel>
        <ExamGrid>
          {examTypeOptions.map((option) => (
            <ExamTypeBtn
              key={option.type}
              onClick={() => setSelectedExamType(option.type)}
              aria-pressed={selectedExamType === option.type}
              style={
                selectedExamType === option.type
                  ? {
                      border: `2px solid ${EPOCUS_PRIMARY}`,
                      backgroundColor: EPOCUS_SELECTED_BG,
                      color: EPOCUS_PRIMARY,
                      fontWeight: 700,
                    }
                  : undefined
              }
            >
              <ExamIconWrap aria-hidden>
                <option.Icon
                  width={28}
                  height={28}
                  fill={
                    selectedExamType === option.type
                      ? EPOCUS_PRIMARY
                      : EPOCUS_ICON_ACTIVE
                  }
                />
              </ExamIconWrap>
              {option.label}
            </ExamTypeBtn>
          ))}
        </ExamGrid>
      </Card>

      {error && (
        <Box
          p={2}
          style={{ backgroundColor: "#fef2f2", borderRadius: 10 }}
          textAlign="center"
        >
          <Typography
            style={{ color: "#dc2626", fontWeight: 600, fontSize: rem(14) }}
          >
            {error}
          </Typography>
        </Box>
      )}

      <SubmitButton
        variant="contained"
        color="primary"
        disabled={
          !selectedExamType ||
          isSubmitting ||
          instancesFetching ||
          assignedFetching
        }
        onClick={handleSubmit}
        fullWidth
      >
        {isSubmitting ? (
          <CircularProgress size={20} style={{ color: EPOCUS_WHITE }} />
        ) : (
          "Add Exam →"
        )}
      </SubmitButton>
    </PageRoot>
  );
};
